/**
 * Where a floating panel stands: the placement maths, and nothing else (issue
 * #103).
 *
 * Every panel that leaves the flow of the page - an infobulle, the fiche of a
 * variable, the fiche of a criterion - answers the same three questions, and
 * they are pure: given the rectangle of the thing it points at, its own size
 * and the size of the window, WHERE does it sit, WHICH side did it end up on,
 * and where along its edge does its arrow point. Written here, they are tested
 * without a DOM; written in a component, they were tested by looking.
 *
 * ## Why this module exists at all
 *
 * `variable-fiche.tsx` grew a `placeBox` of its own in issue #88 - clamp on the
 * x axis, flip on the y - and it was right. A second panel copying it would
 * have been two placers to keep in step, and the third would have settled the
 * question by drifting. So the maths moved here, the fiche calls it, and every
 * panel of issue #104 calls the same one.
 *
 * ## The rule the whole module serves
 *
 * **A floating panel is never cropped.** Not by an ancestor's `overflow`, which
 * is why callers draw in `position: fixed` rather than in the flow, and not by
 * the edge of the window, which is what the flipping and the clamping below are
 * for. A panel that cannot fit on its preferred side takes the opposite one; a
 * panel that fits on neither takes the roomier one and is clamped into the
 * window, because a panel half off-screen is worse than a panel that overlaps
 * what it points at.
 */

/** A side of the anchor, in the order a caller means it: « open upwards ». */
export type FloatingSide = 'top' | 'bottom' | 'left' | 'right';

/** Where the panel sits along the anchor's cross axis. */
export type FloatingAlign = 'start' | 'center' | 'end';

/** The parts of a `DOMRect` the maths uses - so a test writes four numbers. */
export interface FloatingRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface FloatingSize {
  readonly width: number;
  readonly height: number;
}

export interface FloatingRequest {
  /** The rectangle of what the panel points at, in viewport coordinates. */
  readonly anchor: FloatingRect;
  /**
   * The panel's own size, MEASURED.
   *
   * Zero on the first pass, and that is deliberate rather than a gap: whether a
   * panel fits under its anchor depends on a height only the DOM knows, so the
   * caller places it once at zero, reads `offsetHeight`, and places it again.
   * At zero the maths answers the preferred side without flipping, which is the
   * right guess for the single frame before the measure lands.
   */
  readonly floating: FloatingSize;
  readonly viewport: FloatingSize;
  /** The side the caller would like. Default `top`. */
  readonly side?: FloatingSide;
  /** Default `center`. */
  readonly align?: FloatingAlign;
  /** Between the anchor's edge and the panel's. Default `FLOATING_GAP`. */
  readonly gap?: number;
  /** The least space kept between the panel and the window's edge. Default `FLOATING_MARGIN`. */
  readonly margin?: number;
}

export interface FloatingPlacement {
  readonly left: number;
  readonly top: number;
  /** Where it ACTUALLY went, which is what the arrow and the animation follow. */
  readonly side: FloatingSide;
  /**
   * Where the arrow sits along the panel's own edge, in pixels from its left
   * edge (`top` / `bottom` sides) or from its top edge (`left` / `right`).
   *
   * The arrow points at the CENTRE of the anchor, not at the centre of the
   * panel: a clamped panel has moved and its middle no longer names anything.
   * Kept inside the panel's rounded corners by `FLOATING_ARROW_INSET`, so it
   * never hangs off an edge it cannot meet.
   */
  readonly arrow: number;
}

/** The design system's « gap pointe vers déclencheur » - arrow included. */
export const FLOATING_GAP = 8;

/** The least breathing room between a panel and the edge of the window. */
export const FLOATING_MARGIN = 8;

/** How far the arrow stays from the panel's corners, clear of the 8 px radius. */
export const FLOATING_ARROW_INSET = 14;

const OPPOSITE: Readonly<Record<FloatingSide, FloatingSide>> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** How much room the anchor leaves on one side of it, inside the margin. */
function roomOn(side: FloatingSide, anchor: FloatingRect, viewport: FloatingSize, margin: number) {
  switch (side) {
    case 'top':
      return anchor.top - margin;
    case 'bottom':
      return viewport.height - (anchor.top + anchor.height) - margin;
    case 'left':
      return anchor.left - margin;
    case 'right':
      return viewport.width - (anchor.left + anchor.width) - margin;
  }
}

/** The panel's extent along the axis `side` runs on. */
function extentOn(side: FloatingSide, floating: FloatingSize): number {
  return side === 'top' || side === 'bottom' ? floating.height : floating.width;
}

/**
 * The side the panel really takes.
 *
 * Preferred when it fits, the opposite when it does not and that one does, and
 * otherwise whichever of the two is roomier - never a perpendicular side, which
 * would move the panel somewhere the caller did not ask for and the reader does
 * not expect. A panel of unknown height (the first pass, see `floating`) always
 * keeps the preferred side: flipping on a measurement one does not have yet is
 * how a panel comes to jump on the frame after it opens.
 */
function sideFor(
  anchor: FloatingRect,
  floating: FloatingSize,
  viewport: FloatingSize,
  side: FloatingSide,
  gap: number,
  margin: number,
): FloatingSide {
  const need = extentOn(side, floating) + gap;
  // Not measured yet: keep the preferred side rather than flip on a zero.
  if (need === gap) return side;
  if (roomOn(side, anchor, viewport, margin) >= need) return side;
  const other = OPPOSITE[side];
  if (roomOn(other, anchor, viewport, margin) >= extentOn(other, floating) + gap) return other;
  return roomOn(side, anchor, viewport, margin) >= roomOn(other, anchor, viewport, margin)
    ? side
    : other;
}

/** Keep `value` inside the window, but never push a panel wider than the window off its left edge. */
function clamp(value: number, size: number, limit: number, margin: number): number {
  const most = limit - size - margin;
  if (most <= margin) return margin;
  return Math.max(margin, Math.min(value, most));
}

/** Where the panel starts on the cross axis, before clamping. */
function alignedStart(
  align: FloatingAlign,
  anchorStart: number,
  anchorExtent: number,
  floatingExtent: number,
): number {
  switch (align) {
    case 'start':
      return anchorStart;
    case 'center':
      return anchorStart + anchorExtent / 2 - floatingExtent / 2;
    case 'end':
      return anchorStart + anchorExtent - floatingExtent;
  }
}

/**
 * Place a panel against its anchor, in viewport coordinates.
 *
 * The result goes straight into `style={{ left, top }}` on a `position: fixed`
 * element. Nothing here reads the DOM, so a caller measures and calls; a test
 * writes numbers and asserts.
 */
export function placeFloating(request: FloatingRequest): FloatingPlacement {
  const {
    anchor,
    floating,
    viewport,
    side: wanted = 'top',
    align = 'center',
    gap = FLOATING_GAP,
    margin = FLOATING_MARGIN,
  } = request;

  const side = sideFor(anchor, floating, viewport, wanted, gap, margin);
  const vertical = side === 'top' || side === 'bottom';

  const crossStart = alignedStart(
    align,
    vertical ? anchor.left : anchor.top,
    vertical ? anchor.width : anchor.height,
    vertical ? floating.width : floating.height,
  );
  const cross = clamp(
    crossStart,
    vertical ? floating.width : floating.height,
    vertical ? viewport.width : viewport.height,
    margin,
  );

  const reach =
    side === 'top'
      ? anchor.top - floating.height - gap
      : side === 'bottom'
        ? anchor.top + anchor.height + gap
        : side === 'left'
          ? anchor.left - floating.width - gap
          : anchor.left + anchor.width + gap;

  /*
    Le SERRAGE sur l'axe principal, et c'est le défaut qu'une revue a trouvé.

    L'en-tête promet qu'un panneau ne tenant sur aucun des deux côtés est serré
    dans la fenêtre ; seul l'axe transverse l'était. Sur une fenêtre de 800 px,
    une pastille à 380 et une fiche de 500 px, aucun côté ne suffit, le bas
    gagne, et le panneau courait de 406 à 906 - cent pixels hors champ, en
    `position: fixed`, donc irrattrapables au défilement. `placeBox` de
    `variable-fiche.tsx` finissait par un `Math.max(GAP, ...)` qui garantissait
    le contraire, et ce garde s'est perdu au déménagement.

    Le prix est nommé dans l'en-tête et il est assumé : serré, le panneau peut
    recouvrir ce qu'il désigne. Un panneau à moitié hors de l'écran est pire.
  */
  const main = clamp(
    reach,
    vertical ? floating.height : floating.width,
    vertical ? viewport.height : viewport.width,
    margin,
  );

  /*
    The arrow follows the anchor's centre expressed in the panel's own
    coordinates, then stops short of the corners. When the panel is narrower
    than twice the inset there is no room to inset at all, so it sits in the
    middle rather than at a negative offset.
  */
  const anchorMiddle = vertical ? anchor.left + anchor.width / 2 : anchor.top + anchor.height / 2;
  const span = vertical ? floating.width : floating.height;
  const arrow =
    span <= FLOATING_ARROW_INSET * 2
      ? span / 2
      : Math.max(FLOATING_ARROW_INSET, Math.min(anchorMiddle - cross, span - FLOATING_ARROW_INSET));

  return {
    left: vertical ? cross : main,
    top: vertical ? main : cross,
    side,
    arrow,
  };
}
